import { useState, useCallback } from 'react';
import { Loader2, AlertTriangle, CheckCircle, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { api, DuplicateGroup, MergeStrategy } from '@/lib/api';
import { ContactAvatar } from './components/ContactCard';
import { formatContactName } from './utils';
import type { DuplicateSensitivity } from './types';

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-red-100 text-red-800 border-red-200',
  medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  low: 'bg-blue-100 text-blue-800 border-blue-200',
};

export function DuplicatesDialog({
  open,
  onOpenChange,
  sensitivity,
  onComplete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sensitivity: DuplicateSensitivity;
  onComplete: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedPrimaryId, setSelectedPrimaryId] = useState<string | null>(null);
  const [strategy, setStrategy] = useState<MergeStrategy>('keep_primary');
  const [merging, setMerging] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [mergedCount, setMergedCount] = useState(0);

  const scan = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.findDuplicates(sensitivity);
      setGroups(result.groups);
      setCurrentIndex(0);
      setScanned(true);
      if (result.groups.length > 0) {
        setSelectedPrimaryId(result.groups[0].contacts[0].id);
      }
    } catch (err) {
      toast({
        title: 'Failed to scan for duplicates',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [sensitivity]);

  const handleMerge = useCallback(async () => {
    if (!selectedPrimaryId || currentIndex >= groups.length) return;

    const group = groups[currentIndex];
    const mergeIds = group.contacts.filter((c) => c.id !== selectedPrimaryId).map((c) => c.id);

    setMerging(true);
    try {
      await api.mergeContactBookContacts({ primaryId: selectedPrimaryId, mergeIds, strategy });
      setMergedCount((prev) => prev + mergeIds.length);
      toast({ description: `Merged ${mergeIds.length + 1} contacts into one.` });
      advanceToNext();
    } catch (err) {
      toast({
        title: 'Merge failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setMerging(false);
    }
  }, [selectedPrimaryId, currentIndex, groups, strategy]);

  const advanceToNext = useCallback(() => {
    const next = currentIndex + 1;
    if (next < groups.length) {
      setCurrentIndex(next);
      setSelectedPrimaryId(groups[next].contacts[0].id);
    } else {
      setCurrentIndex(groups.length);
    }
  }, [currentIndex, groups]);

  const handleClose = useCallback(() => {
    if (mergedCount > 0) onComplete();
    setGroups([]);
    setScanned(false);
    setCurrentIndex(0);
    setMergedCount(0);
    onOpenChange(false);
  }, [mergedCount, onComplete, onOpenChange]);

  const currentGroup = currentIndex < groups.length ? groups[currentIndex] : null;
  const isDone = scanned && (groups.length === 0 || currentIndex >= groups.length);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Find & Merge Duplicates</DialogTitle>
          <DialogDescription>
            {!scanned
              ? 'Scan your contacts for potential duplicates.'
              : isDone
                ? mergedCount > 0
                  ? `Done! Merged ${mergedCount} duplicate contact${mergedCount !== 1 ? 's' : ''}.`
                  : 'No duplicates found.'
                : `Group ${currentIndex + 1} of ${groups.length}`}
          </DialogDescription>
        </DialogHeader>

        {!scanned && (
          <div className="flex flex-col items-center gap-4 py-8">
            <AlertTriangle className="h-12 w-12 text-muted-foreground" />
            <p className="text-sm text-muted-foreground text-center">
              This will scan all your active contacts for potential duplicates based on your
              sensitivity setting ({sensitivity}).
            </p>
            <Button onClick={scan} disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Scan for Duplicates
            </Button>
          </div>
        )}

        {isDone && scanned && (
          <div className="flex flex-col items-center gap-4 py-8">
            <CheckCircle className="h-12 w-12 text-green-500" />
            <p className="text-sm text-muted-foreground">
              {mergedCount > 0
                ? 'All duplicate groups have been reviewed.'
                : 'Your contacts look clean — no duplicates detected.'}
            </p>
          </div>
        )}

        {currentGroup && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Badge className={CONFIDENCE_COLORS[currentGroup.confidence]}>
                {currentGroup.confidence} confidence
              </Badge>
              <span className="text-sm text-muted-foreground">{currentGroup.matchReason}</span>
            </div>

            <div className="space-y-2">
              {currentGroup.contacts.map((contact) => (
                <div
                  key={contact.id}
                  role="button"
                  tabIndex={0}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedPrimaryId === contact.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:bg-muted/50'
                  }`}
                  onClick={() => setSelectedPrimaryId(contact.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSelectedPrimaryId(contact.id);
                  }}
                >
                  <input
                    type="radio"
                    name="primary"
                    checked={selectedPrimaryId === contact.id}
                    onChange={() => setSelectedPrimaryId(contact.id)}
                    className="flex-shrink-0"
                  />
                  <ContactAvatar contact={contact} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{formatContactName(contact)}</p>
                    {(contact.title || contact.company) && (
                      <p className="text-xs text-muted-foreground truncate">
                        {contact.title}
                        {contact.title && contact.company && ' at '}
                        {contact.company}
                      </p>
                    )}
                    <div className="flex gap-3 text-xs text-muted-foreground mt-0.5">
                      {contact.emails[0] && <span>{contact.emails[0].value}</span>}
                      {contact.phones[0] && <span>{contact.phones[0].value}</span>}
                    </div>
                  </div>
                  {selectedPrimaryId === contact.id && (
                    <Badge variant="secondary" className="text-xs flex-shrink-0">
                      Primary
                    </Badge>
                  )}
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <label className="text-sm font-medium">Merge strategy:</label>
              <Select value={strategy} onValueChange={(v) => setStrategy(v as MergeStrategy)}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_primary">Keep primary fields</SelectItem>
                  <SelectItem value="keep_newest">Keep newest values</SelectItem>
                  <SelectItem value="merge_all">Combine all data</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          {currentGroup ? (
            <div className="flex gap-2 w-full justify-end">
              <Button variant="outline" onClick={advanceToNext} disabled={merging}>
                <SkipForward className="h-4 w-4 mr-1" />
                Skip
              </Button>
              <Button onClick={handleMerge} disabled={merging || !selectedPrimaryId}>
                {merging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Merge
              </Button>
            </div>
          ) : (
            <Button variant="outline" onClick={handleClose}>
              {isDone ? 'Done' : 'Cancel'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
